import {
  type Account,
  AccountPlanType,
} from '../../components/Search/SearchNav/types';
import {
  RampIdType,
  UsageType,
  useBillingPeriodSubscriptionsQuery,
} from '../../generated/graphql';
import {
  getSandboxesSub,
  getSandboxesSubBillingPeriod,
  useAccountPrivileges,
} from '../accounts/pricing';
import {useOrgNameRampFlag} from '../useRampFlag';
import {
  getIntervalMarkersFromCurrentCalendarMonth,
  getIntervalMarkersFromLimitDateInterval,
  getIntervalMarkersFromSubscription,
  useGetUsageThisBillingPeriod,
} from './utils';

function useSandboxesBillingUsageVisibility(orgName: string | null): {
  visible: boolean;
  loading: boolean;
} {
  const hasOrg = orgName != null;

  const {data: enabled, loading: rampLoading} = useOrgNameRampFlag(
    (hasOrg ? orgName : '') as string,
    'sandboxes',
    RampIdType.OrgId
  );

  if (!hasOrg) {
    return {visible: false, loading: false};
  }

  return {
    visible: !rampLoading && enabled,
    loading: rampLoading,
  };
}

/** Call only when `orgName` is known (e.g. from the URL or account selector). */
export function useIsSandboxesBillingUsageVisible(orgName: string): {
  visible: boolean;
  loading: boolean;
} {
  return useSandboxesBillingUsageVisibility(orgName);
}

/**
 * Hook to get Sandboxes usage details for an account.
 *
 * NOTE: This hook is for multi-tenant (SaaS) deployments only. Do NOT use in
 * single-tenant (server) deployments. For server deployments, use the hooks in
 * ServerCurrentBillingPeriodUsageSection.tsx.
 */
export function useGetSandboxesUsageDetails(selectedAccount: Account | null) {
  const orgName = selectedAccount?.name ?? null;
  const {visible: isRampVisible, loading: visibilityLoading} =
    useSandboxesBillingUsageVisibility(orgName);
  const isVisible = isRampVisible && selectedAccount != null;
  const skip =
    selectedAccount?.name == null ||
    visibilityLoading ||
    !isVisible ||
    selectedAccount?.accountPlanType === AccountPlanType.Personal ||
    selectedAccount?.accountPlanType === AccountPlanType.Teams ||
    selectedAccount?.accountPlanType === AccountPlanType.Legacy;

  const privilegeResults = useAccountPrivileges(selectedAccount);
  const {privileges} = privilegeResults;

  const billingPeriodResults = useBillingPeriodSubscriptionsQuery({
    variables: {orgName: skip ? '' : selectedAccount!.name},
    skip,
  });

  const organization = billingPeriodResults.data?.organization ?? undefined;
  const sandboxesStripeSubscription = getSandboxesSub(organization);
  const enterpriseSandboxesSubscription =
    organization?.sandboxesEnterpriseSubscription;

  const isUsagePaid =
    sandboxesStripeSubscription != null ||
    enterpriseSandboxesSubscription != null;

  const sandboxesLimits = {
    ...privileges.sandboxesLimits,
  };
  const enterpriseFreeLimit =
    enterpriseSandboxesSubscription?.sandboxesLimit != null
      ? Number(BigInt(enterpriseSandboxesSubscription.sandboxesLimit))
      : undefined;
  if (enterpriseFreeLimit != null) {
    sandboxesLimits.sandboxesFreeLimit = enterpriseFreeLimit;
  }

  const enterpriseLimitDateInterval =
    enterpriseSandboxesSubscription != null
      ? {
          start: new Date(enterpriseSandboxesSubscription.billingPeriodStart),
          end: new Date(enterpriseSandboxesSubscription.billingPeriodEnd),
        }
      : undefined;
  const finalLimitDateInterval =
    enterpriseLimitDateInterval ?? privileges.sandboxesLimitDateInterval;

  const usagePeriod = selectedAccount?.isEnterprise
    ? getIntervalMarkersFromLimitDateInterval(finalLimitDateInterval)
    : (getIntervalMarkersFromSubscription(
        getSandboxesSubBillingPeriod(
          selectedAccount?.accountPlanType,
          organization
        )
      ) ?? getIntervalMarkersFromCurrentCalendarMonth());

  const usageResults = useGetUsageThisBillingPeriod(
    selectedAccount,
    UsageType.SandboxesCost,
    usagePeriod,
    skip
  );

  const loading =
    visibilityLoading ||
    billingPeriodResults.loading ||
    privilegeResults.loading ||
    usageResults.loading;
  const error =
    billingPeriodResults.error ?? privilegeResults.error ?? usageResults.error;
  const [billingPeriodStart, billingPeriodEnd] = usagePeriod;
  const {usage} = usageResults;

  return {
    isUsagePaid,
    // `isUsagePaid` is a "confidently wrong" `false` while the billing query is
    // still loading; this flag tells consumers when it's not yet trustworthy
    // (see the longer explanation on useGetWbagentUsageDetails, which this
    // mirrors — including why we're not investing in a frontend abstraction).
    isBillingStatusLoading: visibilityLoading || billingPeriodResults.loading,
    sandboxesLimits,
    billingPeriodStart,
    billingPeriodEnd,
    usage,
    loading,
    error,
    isVisible,
    visibilityLoading,
  };
}
